import { Dialog } from '@base-ui/react/dialog';
import { Link } from '@tanstack/react-router';
import { Compass, Download, GitPullRequest, RefreshCw, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { AppShell } from '@/shared/layout/app-shell';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { ConfirmationDialog } from '@/shared/ui/confirmation-dialog';
import { ProgressBar } from '@/shared/ui/progress-bar';
import { LoadingStatus } from '@/shared/ui/spinner';

import { useFieldAdminAccess } from '../api/field-admin-client';
import { downloadFieldAsset } from '../api/field-client';
import { useField, useFieldCommand } from '../api/use-field';
import { useFieldReadState } from '../api/use-field-read-state';
import { useFieldTour } from '../api/use-field-tour';
import { useFieldVoice } from '../api/use-field-voice';
import { useFieldPrCapture, useFieldWorkspace } from '../api/use-field-workspace';
import { FieldAssessment } from '../components/field-assessment';
import { FieldBoard } from '../components/field-board';
import { FieldDiscovery } from '../components/field-discovery';
import { FieldDocuments } from '../components/field-documents';
import { FieldInbox } from '../components/field-inbox';
import { FieldMeetingRoom } from '../components/field-meeting-room';
import { FieldNavigation } from '../components/field-navigation';
import { FieldOperations } from '../components/field-operations';
import { FieldExternalLink, FieldStatus } from '../components/field-primitives';
import { FieldProgressButton } from '../components/field-progress-button';
import { FieldProjectMap } from '../components/field-project-map';
import { FieldReviews } from '../components/field-reviews';
import { FieldToday } from '../components/field-today';
import { FieldTour, FieldTourInvitation } from '../components/field-tour';
import { FieldWorkbench } from '../components/field-workbench';
import { FieldWorkspacePanel } from '../components/field-workspace';
import { fieldToday } from '../field-overview';
import { FIELD_TOUR_STEPS } from '../field-tour-steps';
import { FIELD_SECTIONS, fieldErrorMessage, safeFieldLink } from '../field-view';

import type { FieldDestination } from '../field-overview';
import type { FieldSection } from '../field-view';
import type { FieldActorId, FieldCommand, FieldDocument } from '@mio/shared';

export function FieldPage({ visible = true }: { visible?: boolean }) {
  const query = useField();
  const snapshot = query.data;
  const tour = useFieldTour(snapshot?.discovery ? snapshot.engagement.id : undefined);
  const rememberTourDismissal = tour.dismiss;
  const tourButton = useRef<HTMLButtonElement>(null);
  const command = useFieldCommand(snapshot);
  const capture = useFieldPrCapture(snapshot?.engagement.revision);
  const admin = useFieldAdminAccess();
  const [meetingId, setMeetingId] = useState<string | null>(null);
  const [visitedMeetings, setVisitedMeetings] = useState<Set<string>>(() => new Set());
  const [section, setSection] = useState<FieldSection>('today');
  const workspace = useFieldWorkspace(
    visible && section === 'reviews' && Boolean(snapshot?.capabilities.workspace),
  );
  const [checksPrId, setChecksPrId] = useState<string | undefined>();
  const [visited, setVisited] = useState<Set<FieldSection>>(() => new Set(['today']));
  const [focusRequest, setFocusRequest] = useState(0);
  const [editorRequest, setEditorRequest] = useState(0);
  const [companyRequest, setCompanyRequest] = useState(0);
  const [composeRequest, setComposeRequest] = useState<{
    id: number;
    actorId: FieldActorId;
    text: string;
    sourceIds: string[];
  }>();
  const firstEngagement = useRef<string | null>(null);
  const [focus, setFocus] = useState<Partial<Record<FieldSection, string>>>({});
  const dismissTour = useCallback(() => {
    rememberTourDismissal();
    setFocus({});
  }, [rememberTourDismissal]);
  const [actorId, setActorId] = useState<FieldActorId>('jordan');
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [companyVisible, setCompanyVisible] = useState(true);
  const [endDay, setEndDay] = useState(false);
  const [briefOpen, setBriefOpen] = useState(false);
  const [discoveryView, setDiscoveryView] = useState<{
    tab: 'brief' | 'decisions' | 'findings';
    request: number;
    showDecisionForm?: boolean;
  }>();
  useEffect(() => {
    if (!snapshot || firstEngagement.current === snapshot.engagement.id) return;
    firstEngagement.current = snapshot.engagement.id;
    setDiscoveryView((previous) => ({ tab: 'brief', request: (previous?.request ?? 0) + 1 }));
    setFocus({});
    setMeetingId(null);
    setVisitedMeetings(new Set());
    setComposeRequest(undefined);
    setChecksPrId(undefined);
    setActorId('jordan');
    setBriefOpen(false);
    setEndDay(false);
    setError(null);
    setCompanyRequest((previous) => previous + 1);
    const next = snapshot.discovery ? 'progress' : 'today';
    setSection(next);
    setVisited(new Set(['today', next]));
  }, [snapshot]);
  const voiceSources = useRef<Partial<Record<FieldActorId, string[]>>>({});
  const updateVoiceSources = useCallback((actor: FieldActorId, sourceIds: string[]) => {
    voiceSources.current[actor] = sourceIds;
  }, []);
  const { unreadByActor, unreadCount } = useFieldReadState(
    snapshot,
    visible && companyVisible && section === 'inbox' && !meetingId && tour.step === null
      ? actorId
      : null,
  );
  const act = async (action: FieldCommand['action']) => {
    if (tour.step !== null) dismissTour();
    setError(null);
    try {
      await command.execute(action);
      return true;
    } catch (cause) {
      setError(fieldErrorMessage(cause));
      return false;
    }
  };
  const voiceMessages =
    snapshot?.messages.filter((message) => message.actorId === actorId && !message.meetingId) ?? [];
  const awaitingVoiceReply =
    snapshot?.operations.some(
      (operation) =>
        operation.kind === 'turn' &&
        (operation.status === 'pending' || operation.status === 'running') &&
        voiceMessages.some((message) => message.operationId === operation.id),
    ) ?? false;
  const voice = useFieldVoice(
    actorId,
    voiceMessages,
    (text) =>
      snapshot?.enabled &&
      snapshot.capabilities.voice &&
      snapshot.capabilities.chat &&
      snapshot.engagement.status === 'active'
        ? act({
            type: 'message',
            actorId,
            text,
            sourceIds: voiceSources.current[actorId] ?? [],
            modality: 'voice',
          })
        : Promise.resolve(false),
    {
      engagementId: snapshot?.engagement.id,
      awaitingReply: awaitingVoiceReply,
      inputBlocked: command.isPending || capture.isPending || query.isError,
    },
  );
  const stopVoice = voice.stop;
  const voiceEngagement = useRef(snapshot?.engagement.id);
  useEffect(() => {
    if (voiceEngagement.current === snapshot?.engagement.id) return;
    voiceEngagement.current = snapshot?.engagement.id;
    voiceSources.current = {};
    stopVoice();
  }, [snapshot?.engagement.id, stopVoice]);
  const voiceAvailable =
    snapshot?.enabled &&
    snapshot.capabilities.voice &&
    snapshot.capabilities.chat &&
    snapshot.engagement.status === 'active';
  useEffect(() => {
    if (!visible || !companyVisible || !voiceAvailable) stopVoice();
    if (!visible || !companyVisible) setMeetingId(null);
  }, [visible, companyVisible, voiceAvailable, stopVoice]);
  const navigate = (destination: FieldDestination, touring = false) => {
    if (!touring && tour.step !== null) dismissTour();
    voice.stop();
    const targetMeetingId = destination.meetingId;
    setMeetingId(targetMeetingId ?? null);
    if (targetMeetingId) setVisitedMeetings((previous) => new Set([...previous, targetMeetingId]));
    setSection(destination.section);
    setVisited((previous) => new Set([...previous, destination.section]));
    if (destination.actorId) setActorId(destination.actorId);
    if (destination.draft && destination.actorId)
      setComposeRequest({
        id: Date.now(),
        actorId: destination.actorId,
        text: destination.draft,
        sourceIds: destination.sourceIds ?? [],
      });
    setFocusRequest((request) => request + 1);
    setFocus(destination.focusId ? { [destination.section]: destination.focusId } : {});
    if (destination.section === 'build' && !touring) setEditorRequest((request) => request + 1);
    else setCompanyRequest((request) => request + 1);
  };
  const tourAvailable = Boolean(
    visible &&
    snapshot?.enabled &&
    snapshot.discovery &&
    snapshot.engagement.status === 'active' &&
    !query.isError,
  );
  const tourStep = tour.step;
  // A hidden or unavailable Field page must never leave an active guide behind.
  useEffect(() => {
    if ((!tourAvailable || voice.status !== 'off') && tourStep !== null) {
      dismissTour();
    }
  }, [tourAvailable, tourStep, dismissTour, voice.status]);
  const visitTourStep = (index: number) => {
    const step = FIELD_TOUR_STEPS[index];
    if (
      !tourAvailable ||
      !step ||
      !Number.isInteger(index) ||
      command.isPending ||
      capture.isPending ||
      voice.status !== 'off'
    )
      return;
    setBriefOpen(false);
    setEndDay(false);
    tour.goTo(index);
    const destination = step.destination;
    navigate(
      {
        section: destination.section,
        ...(destination.actorId ? { actorId: destination.actorId } : {}),
        ...(destination.form === 'new-submission' ? { focusId: 'new-submission' } : {}),
      },
      true,
    );
    if (destination.section === 'engagement') {
      setDiscoveryView({
        tab: destination.tab ?? 'brief',
        request: focusRequest + 1,
        showDecisionForm: destination.form === 'decision',
      });
    }
  };
  const closeTour = () => {
    dismissTour();
    tourButton.current?.focus();
  };
  const finishTour = () => {
    tour.complete();
    navigate({ section: 'engagement' }, true);
    setDiscoveryView({ tab: 'brief', request: focusRequest + 1 });
    tourButton.current?.focus();
  };
  const openSubmission = () => {
    const latest = [...(snapshot?.prs ?? [])].sort((a, b) =>
      b.updatedAt.localeCompare(a.updatedAt),
    )[0];
    navigate({ section: 'reviews', focusId: latest?.id ?? 'new-submission' });
  };
  const download = async (
    document: FieldDocument | null,
    format?: FieldDocument['formats'][number],
  ) => {
    setError(null);
    setDownloading(true);
    try {
      await downloadFieldAsset(document, format);
    } catch (cause) {
      setError(fieldErrorMessage(cause));
    } finally {
      setDownloading(false);
    }
  };
  const workspaceHref = safeFieldLink(snapshot?.workspace.href);
  // A progress read receipt never interrupts the learner: failures stay silent and a later
  // snapshot retries. It still serialises with other commands so their expected revision holds.
  const visitingProgress = useRef(false);
  const visitProgress = async (seenRevision: number) => {
    if (command.isPending || capture.isPending) return false;
    visitingProgress.current = true;
    try {
      await command.execute({ type: 'progress.visit', seenRevision });
      return true;
    } catch {
      return false;
    } finally {
      visitingProgress.current = false;
    }
  };
  const busy =
    command.isPending ||
    capture.isPending ||
    query.isError ||
    snapshot?.engagement.status !== 'active';
  const props = snapshot ? { snapshot, busy, onAction: act } : null;
  const work = snapshot ? fieldToday(snapshot) : null;
  const title =
    snapshot?.discovery && section === 'reviews'
      ? 'Reviews'
      : snapshot?.discovery && section === 'engagement'
        ? 'Brief'
        : (FIELD_SECTIONS.find((item) => item.id === section)?.label ?? 'Today');

  return (
    <AppShell title="Workbench" frame="full" compact visible={visible} contentId="field-content">
      <div className="field-page flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden">
        {!snapshot ? (
          <div className="space-y-4 p-8">
            {query.isPending ? (
              <LoadingStatus label="Opening your Workbench engagement…" />
            ) : (
              <>
                <h1 className="page-title">Workbench</h1>
                <Alert>
                  {query.error ? fieldErrorMessage(query.error) : 'Workbench is not available yet.'}
                </Alert>
                <Button
                  variant="secondary"
                  loading={query.isFetching}
                  onClick={() => void query.refetch()}
                >
                  Retry
                </Button>
              </>
            )}
          </div>
        ) : (
          <>
            <header className="field-project-header" aria-label="Workbench project">
              <div className="field-project-context">
                <div className="field-project-identity">
                  <h1>{snapshot.engagement.title}</h1>
                  <p>
                    Harborline Bank <span aria-hidden="true">/</span> Engineering engagement
                  </p>
                </div>
                {snapshot.discovery ? (
                  <FieldProgressButton
                    snapshot={snapshot}
                    active={section === 'progress' && !meetingId && companyVisible}
                    onClick={() => navigate({ section: 'progress' })}
                  />
                ) : null}
              </div>
              <div className="field-project-actions">
                {snapshot.discovery ? (
                  <Button
                    ref={tourButton}
                    variant="secondary"
                    size="sm"
                    disabled={!tourAvailable || busy || voice.status !== 'off'}
                    onClick={() => visitTourStep(0)}
                  >
                    <Compass className="size-4" aria-hidden="true" />
                    Take a tour
                  </Button>
                ) : null}
                <Button size="sm" disabled={busy || !snapshot.enabled} onClick={openSubmission}>
                  <GitPullRequest className="size-4" aria-hidden="true" />
                  Submit for review
                </Button>
                {snapshot.engagement.status !== 'active' ? (
                  <FieldStatus value={snapshot.engagement.status} />
                ) : null}
                {snapshot.discovery ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      navigate({ section: 'engagement', focusId: snapshot.discovery?.brief.id })
                    }
                  >
                    Project brief
                  </Button>
                ) : null}
                <Dialog.Root open={briefOpen} onOpenChange={setBriefOpen}>
                  <Dialog.Trigger
                    render={<Button variant="ghost" size="sm" />}
                    aria-label={snapshot.discovery ? 'Workspace guide' : 'Project brief'}
                  >
                    <span className="field-guide-long">
                      {snapshot.discovery ? 'Workspace guide' : 'Project brief'}
                    </span>
                    <span className="field-guide-short" aria-hidden="true">
                      Guide
                    </span>
                  </Dialog.Trigger>
                  <Dialog.Portal>
                    <Dialog.Backdrop className="fixed inset-0 z-50 bg-[color-mix(in_oklab,var(--fg)_28%,transparent)]" />
                    <Dialog.Popup className="border-line bg-canvas text-fg fixed left-1/2 top-1/2 z-50 max-h-[85dvh] w-[min(92vw,34rem)] -translate-x-1/2 -translate-y-1/2 overflow-auto border p-6 outline-none">
                      <div className="flex items-start justify-between gap-4">
                        <Dialog.Title className="text-xl font-semibold">
                          Your working environment
                        </Dialog.Title>
                        <Dialog.Close
                          render={
                            <Button variant="ghost" size="icon" aria-label="Close project brief" />
                          }
                        >
                          <X size={16} aria-hidden="true" />
                        </Dialog.Close>
                      </div>
                      <Dialog.Description className="text-fg-muted mt-4 whitespace-pre-wrap text-sm leading-7">
                        {snapshot.workspace.instructions}
                      </Dialog.Description>
                      <p className="text-fg-muted mt-4 text-sm leading-6">
                        Submit a working increment whenever you want feedback. Jordan reviews the
                        product experience and Noor reviews the code; choose additional colleagues
                        when needed. Their reports and messages arrive automatically.
                      </p>
                      <div className="mt-5 flex flex-wrap gap-4">
                        {workspaceHref && snapshot.capabilities.workspace ? (
                          <FieldExternalLink href={workspaceHref}>Open workspace</FieldExternalLink>
                        ) : null}
                        {admin.data?.allowed ? (
                          <Link to="/field/admin" className="text-link">
                            Instructor review
                          </Link>
                        ) : null}
                      </div>
                    </Dialog.Popup>
                  </Dialog.Portal>
                </Dialog.Root>
                <Button
                  size="sm"
                  variant="ghost"
                  loading={downloading}
                  disabled={!snapshot.enabled}
                  aria-label="Download starter"
                  data-field-tour="starter-download"
                  onClick={() => void download(null)}
                >
                  <Download className="size-4" aria-hidden="true" />
                  <span className="field-starter-label">Starter files</span>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Refresh Workbench"
                  title="Refresh Workbench"
                  disabled={query.isFetching}
                  onClick={() => void query.refetch()}
                >
                  <RefreshCw
                    className={`size-4 ${query.isFetching ? 'animate-spin' : ''}`}
                    aria-hidden="true"
                  />
                </Button>
                {snapshot.engagement.day < 10 ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={busy || !snapshot.enabled || voice.status !== 'off'}
                    onClick={() => setEndDay(true)}
                  >
                    End day {snapshot.engagement.day}
                  </Button>
                ) : null}
              </div>
              {(command.isPending && !visitingProgress.current) || capture.isPending ? (
                <div className="absolute inset-x-0 bottom-0">
                  <ProgressBar
                    value={null}
                    label={
                      capture.isPending ? 'Capturing Workbench source' : 'Saving Workbench changes'
                    }
                    className="h-0.5"
                  />
                </div>
              ) : null}
            </header>
            {error ? (
              <div className="border-danger/20 bg-danger-soft flex max-h-28 shrink-0 items-start gap-3 overflow-y-auto border-b px-4 py-2">
                <p role="alert" className="text-danger min-w-0 flex-1 text-sm leading-6">
                  {error}
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Dismiss error"
                  onClick={() => setError(null)}
                  className="text-danger shrink-0"
                >
                  <X className="size-4" aria-hidden="true" />
                </Button>
              </div>
            ) : null}
            {query.error ? (
              <div className="shrink-0 px-5 pt-3">
                <Alert>
                  Could not refresh Workbench. The last saved state is shown. Retry before making
                  further changes.
                </Alert>
              </div>
            ) : null}
            {!snapshot.enabled ? (
              <div className="p-5">
                <Alert tone="info">
                  Workbench is currently unavailable. Please try again shortly.
                </Alert>
              </div>
            ) : (
              <FieldWorkbench
                visible={visible}
                editorRequest={editorRequest}
                companyRequest={companyRequest}
                onCompanyVisibilityChange={setCompanyVisible}
                title={title}
                onExploreEditor={() => {
                  if (tour.step !== null) dismissTour();
                }}
                guide={
                  tourAvailable ? (
                    tour.step !== null ? (
                      <FieldTour
                        step={tour.step}
                        onStep={visitTourStep}
                        onClose={closeTour}
                        onFinish={finishTour}
                      />
                    ) : tour.showInvitation && !busy && voice.status === 'off' && companyVisible ? (
                      <FieldTourInvitation onStart={() => visitTourStep(0)} onDismiss={closeTour} />
                    ) : null
                  ) : null
                }
                renderEditor={({ visible: editorVisible, onClose }) => (
                  <FieldWorkspacePanel
                    visible={editorVisible}
                    onClose={onClose}
                    submissionBusy={busy}
                    onSubmit={openSubmission}
                  />
                )}
                navigation={(showCompany, navigation) => (
                  <FieldNavigation
                    section={section}
                    collapsed={navigation.collapsed}
                    onExpand={navigation.onExpand}
                    snapshot={snapshot}
                    unreadCount={unreadCount}
                    onChange={(next) => {
                      navigate({ section: next });
                      showCompany();
                    }}
                    onNavigate={(destination) => {
                      navigate(destination);
                      showCompany();
                    }}
                  />
                )}
              >
                {({ openEditor }) => (
                  <div
                    key={snapshot.engagement.id}
                    className="h-full min-h-0 min-w-0 overflow-hidden"
                  >
                    {props ? (
                      <>
                        {section === 'build' ? (
                          <div className="field-object-panel space-y-5 p-6">
                            <h2 className="text-xl font-semibold" data-field-tour="build-intro">
                              Build your increment
                            </h2>
                            <p className="text-fg-muted text-sm leading-6">
                              Explore the starter, make a working change, and submit it when you
                              want feedback. Open questions can stay open while you prototype.
                            </p>
                            <div className="flex flex-wrap gap-3">
                              <Button onClick={openEditor} data-field-tour="build-editor">
                                Open code editor
                              </Button>
                              <Button variant="secondary" onClick={openSubmission}>
                                Submit for review
                              </Button>
                              <Button
                                variant="ghost"
                                onClick={() => navigate({ section: 'inbox', actorId: 'noor' })}
                              >
                                Ask Noor
                              </Button>
                            </div>
                          </div>
                        ) : null}
                        {snapshot.meetings
                          .filter((meeting) => visitedMeetings.has(meeting.id))
                          .map((meeting) => (
                            <div
                              key={meeting.id}
                              hidden={meetingId !== meeting.id}
                              inert={meetingId !== meeting.id}
                              className="field-section-surface"
                            >
                              <FieldMeetingRoom
                                {...props}
                                meetingId={meeting.id}
                                visible={visible && companyVisible && meetingId === meeting.id}
                                focusId={meetingId === meeting.id ? focus.inbox : undefined}
                                focusRequest={focusRequest}
                                onLeave={() => setMeetingId(null)}
                              />
                            </div>
                          ))}
                        <div
                          hidden={Boolean(meetingId) || section !== 'today'}
                          inert={Boolean(meetingId) || section !== 'today'}
                          className="field-section-surface"
                        >
                          <FieldToday
                            snapshot={snapshot}
                            unreadByActor={unreadByActor}
                            onNavigate={navigate}
                            onOpenEditor={openEditor}
                            onAction={act}
                            busy={busy}
                          />
                        </div>
                        {visited.has('progress') && snapshot.discovery ? (
                          <div
                            hidden={Boolean(meetingId) || section !== 'progress'}
                            inert={Boolean(meetingId) || section !== 'progress'}
                            className="field-section-surface"
                          >
                            <FieldProjectMap
                              {...props}
                              unreadByActor={unreadByActor}
                              onNavigate={navigate}
                              onVisit={visitProgress}
                              visible={
                                visible &&
                                companyVisible &&
                                section === 'progress' &&
                                !meetingId &&
                                tour.step === null
                              }
                            />
                          </div>
                        ) : null}
                        {visited.has('engagement') && snapshot.discovery ? (
                          <div
                            hidden={Boolean(meetingId) || section !== 'engagement'}
                            inert={Boolean(meetingId) || section !== 'engagement'}
                            className="field-section-surface"
                          >
                            <FieldDiscovery
                              {...props}
                              onNavigate={navigate}
                              onOpenEditor={openEditor}
                              focusId={focus.engagement}
                              focusRequest={focusRequest}
                              viewRequest={discoveryView}
                              onViewChange={() => {
                                if (tour.step !== null) dismissTour();
                              }}
                            />
                          </div>
                        ) : null}
                        {visited.has('inbox') ? (
                          <div
                            hidden={Boolean(meetingId) || section !== 'inbox'}
                            inert={Boolean(meetingId) || section !== 'inbox'}
                            className="field-section-surface"
                          >
                            <FieldInbox
                              {...props}
                              actorId={actorId}
                              composeRequest={composeRequest}
                              onVoiceReferencesChange={updateVoiceSources}
                              onActorChange={(nextActor) => {
                                if (tour.step !== null) dismissTour();
                                setActorId(nextActor);
                              }}
                              onNavigate={navigate}
                              voice={voice}
                              unreadByActor={unreadByActor}
                              focusId={meetingId ? undefined : focus.inbox}
                              focusRequest={focusRequest}
                            />
                          </div>
                        ) : null}
                        {visited.has('board') ? (
                          <div
                            hidden={Boolean(meetingId) || section !== 'board'}
                            inert={Boolean(meetingId) || section !== 'board'}
                            className="field-section-surface"
                          >
                            <FieldBoard
                              {...props}
                              focusId={focus.board}
                              focusRequest={focusRequest}
                              onNavigate={navigate}
                              onOpenMeeting={(id) => {
                                if (tour.step !== null) dismissTour();
                                voice.stop();
                                setFocus({});
                                setVisitedMeetings((previous) => new Set([...previous, id]));
                                setMeetingId(id);
                              }}
                            />
                          </div>
                        ) : null}
                        {visited.has('reviews') ? (
                          <div
                            hidden={Boolean(meetingId) || section !== 'reviews'}
                            inert={Boolean(meetingId) || section !== 'reviews'}
                            className="field-section-surface"
                          >
                            <FieldReviews
                              {...props}
                              onNavigate={navigate}
                              focusId={focus.reviews}
                              focusRequest={focusRequest}
                              suppressFocus={tour.step !== null}
                              captureReady={Boolean(
                                !workspace.isError &&
                                workspace.data?.state === 'ready' &&
                                workspace.data.selection === 'field' &&
                                workspace.data.context &&
                                !['pending', 'running'].includes(
                                  workspace.data.operation?.status ?? '',
                                ),
                              )}
                              onOpenEditor={openEditor}
                              onRunChecks={(prId) => {
                                setChecksPrId(prId);
                                navigate({ section: 'operations' });
                              }}
                              onCapture={async (input) => {
                                if (tour.step !== null) dismissTour();
                                setError(null);
                                try {
                                  await capture.mutateAsync(input);
                                  return true;
                                } catch (cause) {
                                  setError(fieldErrorMessage(cause));
                                  return false;
                                }
                              }}
                            />
                          </div>
                        ) : null}
                        {visited.has('documents') ? (
                          <div
                            hidden={Boolean(meetingId) || section !== 'documents'}
                            inert={Boolean(meetingId) || section !== 'documents'}
                            className="field-section-surface"
                          >
                            <FieldDocuments
                              {...props}
                              focusId={focus.documents}
                              focusRequest={focusRequest}
                              onDownload={(document, format) => void download(document, format)}
                              downloading={downloading}
                            />
                          </div>
                        ) : null}
                        {visited.has('operations') ? (
                          <div
                            hidden={Boolean(meetingId) || section !== 'operations'}
                            inert={Boolean(meetingId) || section !== 'operations'}
                            className="field-section-surface"
                          >
                            <FieldOperations
                              {...props}
                              focusId={focus.operations}
                              focusRequest={focusRequest}
                              candidateId={checksPrId}
                              onAskRelease={async (prId) => {
                                const pr = snapshot.prs.find((candidate) => candidate.id === prId);
                                if (!pr) return false;
                                const evidence = (['acceptance', 'rollback'] as const).flatMap(
                                  (suite) => {
                                    const run = snapshot.runs
                                      .filter(
                                        (candidate) =>
                                          candidate.prId === pr.id &&
                                          candidate.revision === pr.revision &&
                                          candidate.suite === suite &&
                                          candidate.trusted &&
                                          ['passed', 'failed'].includes(candidate.status),
                                      )
                                      .at(-1);
                                    return run ? [run.id] : [];
                                  },
                                );
                                const sent = await act({
                                  type: 'message',
                                  actorId: 'amara',
                                  text: `Please review release readiness and release “${pr.title}” (PR ${pr.id}, revision ${pr.revision}) to the synthetic cohort if all current requirements are met. If blocked, explain what is missing.`,
                                  sourceIds: [pr.id, ...evidence],
                                  modality: 'chat',
                                });
                                if (sent) navigate({ section: 'inbox', actorId: 'amara' });
                                return sent;
                              }}
                            />
                          </div>
                        ) : null}
                        {visited.has('assessment') ? (
                          <div
                            hidden={Boolean(meetingId) || section !== 'assessment'}
                            inert={Boolean(meetingId) || section !== 'assessment'}
                            className="field-section-surface"
                          >
                            <FieldAssessment
                              {...props}
                              onNavigate={(section) => navigate({ section })}
                            />
                          </div>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                )}
              </FieldWorkbench>
            )}
            <ConfirmationDialog
              open={endDay}
              title={`End day ${snapshot.engagement.day}`}
              confirmLabel={`Continue to day ${snapshot.engagement.day + 1}`}
              cancelLabel="Keep working"
              busy={command.isPending}
              confirmDisabled={busy || !snapshot.enabled || voice.status !== 'off'}
              onCancel={() => setEndDay(false)}
              onConfirm={() => {
                void act({ type: 'day.advance' }).then((success) => {
                  if (success) {
                    setEndDay(false);
                    navigate({ section: 'today' });
                  }
                });
              }}
            >
              <p>
                Move the engagement forward when you are ready. Your open work stays on the board.
              </p>
              <ul className="mt-4 space-y-2">
                <li>
                  {work?.done ?? 0} of {snapshot.tickets.length} tickets complete
                </li>
                <li>{work?.activeTickets.length ?? 0} current tickets still open</li>
                <li>{unreadCount} unread colleague messages on this device</li>
                {work?.running.length ? (
                  <li>{work.running.length} background tasks still in progress</li>
                ) : null}
              </ul>
              <p className="mt-4">
                Post your team standup from Tickets before moving on, if you have not already.
              </p>
            </ConfirmationDialog>
          </>
        )}
      </div>
    </AppShell>
  );
}
